import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, ShieldCheck, AlertCircle } from "lucide-react";

/**
 * Short-link redirect for COA PDFs. The SMS shipping notification uses
 * `glpflo.org/c/{coa_id}` (saves ~50 chars vs the full `/api/coa/.../file`
 * URL) so we have room to also fit the carrier tracking link in one SMS.
 *
 * We do a HEAD request first so if the COA was soft-deleted between the
 * SMS send and the click we land the patient on a friendly "this lot is
 * being re-verified" page instead of a raw 404 PDF response.
 */
export default function CoaShortLink() {
  const { id } = useParams();
  const [status, setStatus] = useState("checking"); // checking | redirecting | missing
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/coa/${id}/file`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(fileUrl, { method: "HEAD" });
        if (cancelled) return;
        if (r.ok) {
          setStatus("redirecting");
          window.location.replace(fileUrl);
        } else {
          setStatus("missing");
        }
      } catch {
        if (!cancelled) setStatus("missing");
      }
    })();
    return () => { cancelled = true; };
  }, [fileUrl]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="coa-shortlink-page">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
        {status === "missing" ? (
          <>
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              This Certificate isn&apos;t available yet
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              The Certificate of Analysis for your shipment lot is being processed by the pharmacy.
              It&apos;ll appear in your dashboard the moment it&apos;s uploaded — usually within 1 business day of shipment.
            </p>
            <Link
              to="/coa"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition"
              data-testid="coa-shortlink-fallback-cta"
            >
              <FileText className="w-4 h-4" /> Search by lot number
            </Link>
          </>
        ) : (
          <>
            <ShieldCheck className="w-12 h-12 text-emerald-600 mx-auto mb-4 animate-pulse" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Opening your Certificate of Analysis
            </h1>
            <p className="text-sm text-slate-600">Verifying potency, sterility, and identity for your lot…</p>
          </>
        )}
      </div>
    </div>
  );
}
